import { use, useEffect } from 'react';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';

import { ReadSystemsResponse } from 'features/systems/domain/models/read-systems-response';
import { useGetSystems } from 'features/systems/domain/queries/systems-queries';
import useSystemDetails, { SystemDetailsHook } from 'features/systems/hooks/system-details-hook';
import useSystemFilters, { SystemFiltersHook } from 'features/systems/hooks/system-filters-hook';

export interface SystemsHook {
  systemsResponse?: ReadSystemsResponse;
  systemsIsLoading: boolean;
  systemsIsSuccess: boolean;

  systemsFilterHook: SystemFiltersHook;
  systemsDetailsHook: SystemDetailsHook;

  openDetailsInViewingMode: (systemId?: string) => void;
  openDetailsInEditingMode: (systemId?: string) => void;

  refresh: () => void;
}

const useSystems = (): SystemsHook => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);

  const systemsFilterHook = useSystemFilters();

  const {
    mutate: triggerReadSystems,
    data: systemsResponse,
    error,
    isError,
    isPending: systemsIsLoading,
    isSuccess: systemsIsSuccess,
  } = useGetSystems();

  const refresh = (): void => {
    triggerReadSystems(systemsFilterHook.readSystemsQuery);
    systemsFilterHook.loadMonitorFilters();
  };

  const systemsDetailsHook = useSystemDetails(refresh);

  useEffect(() => {
    triggerReadSystems(systemsFilterHook.readSystemsQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemsFilterHook.readSystemsQuery]);

  useEffect(() => {
    if (isError && error) {
      setErrorMessage(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  const openDetailsInViewingMode = (systemId?: string): void => {
    systemsDetailsHook.openDetailsInViewingMode(systemId);
  };

  const openDetailsInEditingMode = (systemId?: string): void => {
    systemsDetailsHook.openDetailsInEditingMode(systemId);
  };
  return {
    systemsResponse,
    systemsIsLoading,
    systemsIsSuccess,

    systemsFilterHook,
    systemsDetailsHook,

    openDetailsInViewingMode,
    openDetailsInEditingMode,

    refresh,
  };
};

export default useSystems;
